import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';

import { Close } from '@mui/icons-material';
import { Box, Checkbox, Drawer, FormControlLabel, MenuItem, Modal, Select, Stack, Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledDatePicker from 'components/LabelledDatePicker';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import {
  useApproveScopeByScopeIdMutation,
  useCreateProjectScopeMutation,
  useLazyGetProjectScopeByIdQuery,
  useUpdateProjectScopeByIdMutation,
} from 'services/busmanApi';
import { ProjectScopeRequest } from 'types/ProjectTypes';
import { PROJECT_SCOPE_STATUS } from 'utils/constants';
import { ProjectScopeFormValues, ProjectScopeSchema } from 'utils/schema';

import { ColContainer, FormSection } from '../../ProjectManagement/styles';

const defaultScopeValues = {
  project: '',
  parent_scope: null,
  version: '',
  status: String(PROJECT_SCOPE_STATUS.draft.value),
  change_summary: '',
  cost_delta: 0,
  hours_delta: 0,
  effective_from: null,
  effective_till: null,
  is_client_approved: false,
};

interface EditScopeModalProps {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  parentScopeId?: string | null;
  initialValues: ProjectScopeRequest | null;
  setSnackBarMessage: (message: MessageType) => void;
}

const EditScopeModal: React.FC<EditScopeModalProps> = ({
  initialValues,
  open,
  onClose,
  isEditing,
  parentScopeId = null,
  setSnackBarMessage,
}) => {
  const { projectID } = useParams();
  const [createScope] = useCreateProjectScopeMutation();
  const [updateScope] = useUpdateProjectScopeByIdMutation();
  const [triggerRefetch] = useLazyGetProjectScopeByIdQuery();
  const [approveScope] = useApproveScopeByScopeIdMutation();
  const [confirmApprove, setConfirmApprove] = React.useState<boolean>(false);

  const {
    handleSubmit,
    control,
    setError,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: initialValues ?? { ...defaultScopeValues, parent_scope: parentScopeId },
    resolver: zodResolver(ProjectScopeSchema),
  });

  const projectScope = (() => {
    if (!isEditing) {
      return Object.entries(PROJECT_SCOPE_STATUS).filter(([, value]) => value.value === 'DRAFT');
    }
    if (initialValues?.status !== 'APPROVED' && !initialValues?.is_client_approved) {
      return Object.entries(PROJECT_SCOPE_STATUS).filter(
        ([, value]) => value.value !== 'APPROVED' && value.value !== 'SUPERSEDED',
      );
    }
    return Object.entries(PROJECT_SCOPE_STATUS);
  })();

  const onSubmit = async (projectScopeData: ProjectScopeFormValues) => {
    const payload = {
      ...projectScopeData,
      project: projectID ?? '',
      approved_by_id: projectScopeData.is_client_approved ? initialValues?.project_client : '',
      parent_scope: projectScopeData.parent_scope ?? parentScopeId,
      effective_from: projectScopeData.effective_from
        ? dayjs(projectScopeData.effective_from).format('YYYY-MM-DD')
        : null,
      effective_till: projectScopeData.effective_till
        ? dayjs(projectScopeData.effective_till).format('YYYY-MM-DD')
        : null,
    };

    const showResult = async (
      response: { data?: { id?: string }; error?: unknown },
      verb: 'adding' | 'updating',
      pastTense: string,
    ) => {
      setSnackBarMessage(
        response.error
          ? { type: 'error', msg: `Error ${verb} project scope` }
          : { type: 'success', msg: `Project scope ${pastTense} successfully.` },
      );

      if (!response.error) {
        const scopeId = response.data?.id;
        if (scopeId) {
          await triggerRefetch(scopeId);
        }
      }
    };

    if (isEditing && initialValues) {
      await showResult(await updateScope(payload), 'updating', 'updated');
    } else {
      await showResult(await createScope(payload), 'adding', 'added');
    }

    onClose();
  };

  const onApprove = async (projectScopeData: ProjectScopeFormValues) => {
    let hasError = false;

    if (projectScopeData.effective_from === null) {
      setError('effective_from', { type: 'required', message: 'Effective from date required for scope approval.' });
      hasError = true;
    }

    if (projectScopeData.effective_till === null) {
      setError('effective_till', { type: 'required', message: 'Effective till date required for scope approval.' });
      hasError = true;
    }

    if (!projectScopeData.is_client_approved) {
      setError('is_client_approved', {
        type: 'required',
        message: 'Following scope needs to be approved by client for scope approval.',
      });
      hasError = true;
    }

    if (projectScopeData.status === PROJECT_SCOPE_STATUS.rejected.value) {
      setSnackBarMessage({ type: 'error', msg: 'Rejected project scope cannot be approved' });
      setConfirmApprove(false);
      return;
    }

    if (projectScopeData.change_summary.length === 0) {
      setError('change_summary', { type: 'required', message: 'Change summary is required for scope approval.' });
      hasError = true;
    }

    if (projectScopeData.version.length === 0) {
      setError('version', { type: 'required', message: 'Version is required for scope approval' });
      hasError = true;
    }

    if (!projectScopeData.project) {
      setSnackBarMessage({ type: 'error', msg: 'Error project id is undefined' });
      setConfirmApprove(false);
      return;
    }

    if (initialValues && initialValues.project_client?.length === 0) {
      setSnackBarMessage({ type: 'error', msg: 'Project Client is required' });
      setConfirmApprove(false);
      return;
    }

    if (hasError) {
      setConfirmApprove(false);
      return;
    }

    const response = await approveScope({
      ...projectScopeData,
      project: projectScopeData.project,
      approved_by_id: initialValues?.project_client,
    });

    if ('error' in response) {
      setSnackBarMessage({ type: 'error', msg: 'Error approving project scope' });
    }

    if ('success' in response) {
      setSnackBarMessage({ type: 'success', msg: 'Project scope has been approved' });
    }

    if (!response.error) {
      const scopeId = response.data?.id ?? initialValues?.id;
      if (scopeId) triggerRefetch(scopeId);
    }

    onClose();
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Project Scope' : 'New Project Scope'}</Typography>
            <Box onClick={onClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection>
            <Stack direction="row" gap={2}>
              <LabelledTextField name="version" label="VERSION" control={control} />
              <Controller
                name="status"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ColContainer>
                    <Typography variant="body2">STATUS</Typography>
                    <Select
                      size="small"
                      sx={{ width: '100%' }}
                      label="status"
                      value={field.value}
                      error={!!error}
                      onChange={(selectedItem) => field.onChange(selectedItem.target.value)}
                      data-testid="scope-status-select-option">
                      {projectScope.map(([key, value]) => (
                        <MenuItem key={key} value={String(value.value)}>
                          {value.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                  </ColContainer>
                )}
              />
            </Stack>

            <LabelledTextField name="change_summary" label="CHANGE SUMMARY" control={control} multiline />

            {/* Deltas */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5">Deltas</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <Stack direction="row" gap={2}>
                  <LabelledTextField name="cost_delta" label="COST DELTA" control={control} type="number" step="0.01" />
                  <LabelledTextField
                    name="hours_delta"
                    label="HOURS DELTA"
                    control={control}
                    type="number"
                    step="0.01"
                  />
                </Stack>
              </Box>
            </Box>

            {/* Effective period */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5">Effective Period</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <Stack direction="row" gap={2}>
                  <LabelledDatePicker name="effective_from" label="EFFECTIVE FROM" control={control} />
                  <LabelledDatePicker name="effective_till" label="EFFECTIVE TILL" control={control} />
                </Stack>
              </Box>
            </Box>

            <Controller
              name="is_client_approved"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />
                    }
                    label="Client approved"
                  />
                  {errors.is_client_approved && (
                    <FormHelperText sx={{ color: 'red' }}>*{errors.is_client_approved.message}</FormHelperText>
                  )}
                </>
              )}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Stack direction="column" sx={{ mt: 2 }} gap={2}>
                <CustomButton
                  text={`${isEditing ? 'Update' : 'Add'}`}
                  variant="contained"
                  width="medium"
                  testid={'add-scope-modal-btn'}
                  onClick={handleSubmit(onSubmit)}
                  disabled={isEditing && !isDirty}
                />

                {isEditing &&
                  initialValues?.status !==
                    (PROJECT_SCOPE_STATUS.approved.value || PROJECT_SCOPE_STATUS.superseded.value) && (
                    <CustomButton
                      text="Approve scope"
                      width="medium"
                      variant="contained"
                      testid="approve-scope-modal-btn"
                      onClick={() => setConfirmApprove(true)}
                    />
                  )}
              </Stack>
            </Box>
          </FormSection>
        </Box>
      </Drawer>
      {confirmApprove && initialValues && (
        <Modal
          open={confirmApprove}
          onClose={() => setConfirmApprove(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box
            sx={{
              p: 2,
              top: '50%',
              left: '50%',
              width: 450,
              position: 'absolute',
              bgcolor: 'background.paper',
              transform: 'translate(-50%, -50%)',
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
            }}>
            <Header>
              <Typography variant="h4">Approve scope Project</Typography>
              <Box onClick={handleSubmit(onApprove)} sx={{ cursor: 'pointer' }}>
                <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
              </Box>
            </Header>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Approve the selected project scope ?
            </Typography>
            <Stack sx={{ justifyContent: 'flex-end', mt: 2 }}>
              <CustomButton
                text={'Approve'}
                variant="contained"
                width="medium"
                testid={'add-project-modal-btn'}
                onClick={handleSubmit(onApprove)}
              />
            </Stack>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default EditScopeModal;
